import {Grid} from "@mui/material";
import {CopyUtils} from "../../util/CopyUtils";
import {useTranslation} from "react-i18next";
import TextField from "../../common/form/TextField";
import {IsdsMessageFilterDto, IsdsMessageState} from "../../client/message/IsdsMessageApiClient";
import {MessageTranslKey} from "./MessageTranslKey";
import {DateTimeFormatter} from "../../common/DateTimeFormatter";
import DateField from "../../common/form/DateField";
import {MessageStatusFormatter} from "./MessageStatusFormatter";
import SingleSelect from "../../common/form/SingleSelect";

export interface MessageFilterFormProps {
    filter: IsdsMessageFilterDto;
    onChange: (filter: IsdsMessageFilterDto) => void;
}

export function MessageFilterForm(props: Readonly<MessageFilterFormProps>) {
    const {filter, onChange} = props;

    const {t} = useTranslation();

    function onMessageStateChange(value: unknown) {
        const stringValue = typeof value === "string" ? value.trim() : "";

        let nextState: IsdsMessageState | undefined;
        if (Object.values(IsdsMessageState).includes(stringValue as IsdsMessageState)) {
            nextState = stringValue as IsdsMessageState;
        } else {
            nextState = undefined;
        }

        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.currentStateType = nextState;
        onChange(updatedFilter);
    }

    const messageStatusOptions = [
        { value: "", label: t(MessageTranslKey.ALL_CURRENT_STATES)},
        ...Object.values(IsdsMessageState).map(state => ({
            value: state,
            label: MessageStatusFormatter.formatMessageStatusStatus(state, t),
        }))
    ];

    function onMessageRecipientChange(recipient: string) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.recipient = normalizeString(recipient);
        onChange(updatedFilter);
    }

    function onMessageCreateToChange(createdTo: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        const isoFormatCreatedTo = DateTimeFormatter.convertDateToIsoFormat(createdTo);
        updatedFilter.createdTo = DateTimeFormatter.formatAsIsoDateTimeAtEndOfDay(isoFormatCreatedTo);
        onChange(updatedFilter);
    }

    function onMessageCreatedSinceChange(createdSince: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.createdSince = DateTimeFormatter.convertDateToIsoFormat(createdSince);
        onChange(updatedFilter);
    }

    function normalizeString(value: string): string | undefined {
        const normalizedValue = value.trim();
        return (normalizedValue.length > 0) ? normalizedValue : undefined;
    }

    return (
        <Grid container mt={0} spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <SingleSelect label={t(MessageTranslKey.CURRENT_STATE)}
                              options={messageStatusOptions}
                              value={filter.currentStateType ?? ""}
                              onChange={onMessageStateChange} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <TextField label={t(MessageTranslKey.RECIPIENT)}
                           value={filter.recipient}
                           onChange={onMessageRecipientChange} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <DateField label={t(MessageTranslKey.CREATE_SINCE)}
                           value={DateTimeFormatter.convertFromIsoDateTimeString(filter.createdSince)}
                           onChange={onMessageCreatedSinceChange}/>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <DateField label={t(MessageTranslKey.CREATE_TO)}
                           value={DateTimeFormatter.convertFromIsoDateTimeString(filter.createdTo)}
                           onChange={onMessageCreateToChange}/>
            </Grid>
        </Grid>
    );
}
